// src/pages/OrdersHistory.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, Gift, Loader2, RefreshCw, Trash2, Ticket } from 'lucide-react';

const OrdersHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repeating, setRepeating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const currentUserId = tgUser?.id;

  useEffect(() => {
    if (!currentUserId) {
      setLoading(false);
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const fetchOrders = async () => {
    try {
      if (!currentUserId) return;
      const res = await axios.get(`/api/profile?user_id=${currentUserId}`);
      if (res.data && res.data.orders) {
        setOrders(res.data.orders);
      }
    } catch (e) {
      console.error("Orders fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRepeatOrder = async (orderId) => {
      if (!window.confirm("Оформить этот заказ повторно?")) return;
      setRepeating(orderId);
      try {
          const res = await axios.post(`/api/repeat-order/${orderId}`, { user_id: currentUserId });
          if (res.data.status === 'ok') {
              alert(`Заказ успешно повторен! Новый номер: #${res.data.order_id}`);
              fetchOrders();
          } else {
              alert("Ошибка: " + (res.data.error || "Не удалось повторить"));
          }
      } catch (e) { alert("Ошибка соединения с сервером"); } finally { setRepeating(null); }
  };

  const handleDeleteOrder = async (orderId) => {
      if (!window.confirm("Удалить этот заказ из истории?")) return;
      setDeleting(orderId);
      try {
          await axios.delete(`/api/orders/${orderId}?user_id=${currentUserId}`);
          setOrders(prev => prev.filter(o => o.order_id !== orderId));
      } catch (e) { alert("Не удалось удалить заказ."); } finally { setDeleting(null); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Новый': return 'bg-blue-100 text-blue-600';
      case 'Готовится': return 'bg-yellow-100 text-yellow-600';
      case 'В доставке': return 'bg-purple-100 text-purple-600';
      case 'Выполнен': return 'bg-green-100 text-green-600';
      case 'Отменен': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const safeDate = dateString.replace(" ", "T");
        const date = new Date(safeDate);
        if (isNaN(date.getTime())) return dateString; 
        return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
    } catch { return dateString; }
  };

  // --- CHANGED: Функция извлечения данных о промокоде ---
  const extractPromoDetails = (comment) => {
      if (!comment) return null;
      // Ищем строку вида [ПРОМОКОД: GOGO (-150₽)]
      const match = comment.match(/\[ПРОМОКОД:\s*([A-Z0-9]+)\s*\(-(\d+(\.\d+)?)₽\)\]/i);
      if (match) {
          return { code: match[1], discount: Math.floor(parseFloat(match[2])) };
      }
      return null;
  };

  if (loading) return (
    <div className="min-h-screen bg-tg-bg flex items-center justify-center"><Loader2 className="animate-spin text-tg-hint" /></div>
  );

  return (
    <div className="min-h-screen bg-tg-bg pb-20">
      <div className="px-4 pb-4 pt-tg flex items-center sticky top-0 bg-tg-bg/95 backdrop-blur-md z-10 border-b border-gray-100/5">
        <button onClick={() => navigate('/profile')} className="mr-3 text-tg-text p-1 active:opacity-60"><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-bold text-tg-text">Мои заказы</h1>
      </div>

      <div className="p-4 space-y-4">
        {orders.length === 0 ? (
          <div className="text-center py-20 opacity-50">
            <Package size={48} className="mx-auto mb-3 text-tg-hint" />
            <p className="text-tg-text font-medium">История заказов пуста</p>
          </div>
        ) : (
          orders.map((order) => {
            const promoInfo = extractPromoDetails(order.comment); // Извлекаем промокод
            
            return (
              <div key={order.order_id} className="bg-tg-secondary-bg p-5 rounded-2xl shadow-sm border border-transparent relative">
                
                <div className="flex justify-between items-start mb-4">
                  <div>
                      <span className="text-xs font-bold text-tg-hint uppercase tracking-wider">Заказ #{order.order_id}</span>
                      <p className="text-tg-text text-sm font-medium opacity-70 mt-0.5">{formatDate(order.order_date)}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>{order.status}</span>
                </div>

                <div className="space-y-3 mb-4">
                  {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                          <div className="flex-1 pr-2">
                              <span className="text-tg-text font-medium line-clamp-2">{item.name}</span>
                              {item.mods && item.mods.length > 0 && <span className="text-xs text-tg-hint block mt-0.5">+ {item.mods.map(m => m.name).join(', ')}</span>}
                              <span className="text-xs text-tg-hint mt-1 block">x{item.quantity}</span>
                          </div>
                          <span className="font-bold text-tg-text whitespace-nowrap">
                              {item.line_total ? item.line_total.toFixed(0) : (item.price * item.quantity).toFixed(0)} ₽
                          </span>
                      </div>
                  ))}
                </div>

                <div className="h-px bg-tg-text/5 my-3" />

                {/* --- CHANGED: Блок визуализации скидок (Промокод и Бонусы) --- */}
                <div className="mb-3 space-y-1.5 flex flex-col items-end">
                    {promoInfo && (
                        <div className="flex items-center gap-2 text-[11px] font-bold text-green-500 bg-green-500/10 px-2 py-1 rounded-lg">
                            <Ticket size={14} />
                            <span className="uppercase">{promoInfo.code}</span>
                            <span>-{promoInfo.discount} ₽</span>
                        </div>
                    )}
                    {order.used_bonuses > 0 && (
                        <div className="flex items-center gap-1.5 text-orange-500 text-[11px] font-bold bg-orange-500/10 px-2 py-1 rounded-lg">
                            <Gift size={12} />
                            <span>Списано: -{order.used_bonuses} ₽</span>
                        </div>
                    )}
                </div>
                {/* -------------------------------------------------------- */}

                <div className="flex justify-between items-end">
                    <div className="flex flex-col gap-1">
                        <span className="text-xl font-extrabold text-tg-text">{order.total_price.toFixed(0)} ₽</span>
                        {order.earned_bonuses > 0 && (
                            <div className="flex items-center gap-1.5 text-green-500 text-xs font-bold mt-1">
                                <Gift size={12} />
                                <span>+{order.earned_bonuses} бонусов</span>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <button onClick={() => handleRepeatOrder(order.order_id)} disabled={repeating === order.order_id} className={`flex items-center gap-1 text-white text-sm font-bold bg-blue-500 px-4 py-2 rounded-xl active:scale-95 transition-transform shadow-sm ${repeating === order.order_id ? 'opacity-50' : ''}`}>
                            {repeating === order.order_id ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                            <span>Повторить</span>
                        </button>
                        <button onClick={() => handleDeleteOrder(order.order_id)} disabled={deleting === order.order_id} className="flex items-center justify-center w-10 h-10 text-red-500 bg-red-500/10 rounded-xl active:scale-95 transition-transform">
                            {deleting === order.order_id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                        </button>
                    </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OrdersHistory;
